/**
 * Doutes de l'algorithme local — les cas limites qu'il n'a pas su trancher,
 * formulés pour la passe IA de grouping. L'algorithme propose, l'IA juge :
 * chaque doute attire son attention sur un point précis du découpage.
 *
 * Module pur et testable : il relit le résultat du regroupement et les
 * entités scannées, sans toucher au moteur.
 */

import { bboxGap } from "./geometry";
import {
  NEAR_GAP_MAX_RATIO,
  ATTACH_GAP_RATIO,
} from "./grouping-constants";
import { MANY_MARKERS_COUNT } from "./confidence";
import { classifyEntity } from "./grouping";
import type {
  AnalysisEntity,
  PlanAnalysisResult,
  WorldBbox,
} from "./types";

/** Doute transmis à la passe IA de grouping. */
export interface AnalysisDoubt {
  /** Type machine (stable, utilisable pour les stats de calibration). */
  type:
    | "near_groups"
    | "multi_tip_text"
    | "many_markers"
    | "orphan_near"
    | "photo_candidates";
  /** Explication courte, lisible par l'IA. */
  message: string;
  entityIds?: string[];
}

/**
 * Au-delà de 1× le plafond de liaison mais en dessous de ce multiple, deux
 * groupes du même désordre sont « relativement proches » : l'algorithme a
 * tranché la séparation, mais le signale à l'IA.
 */
const NEAR_GROUPS_DOUBT_FACTOR = 2;

/** Même logique pour un texte orphelin presque rattachable. */
const ORPHAN_DOUBT_FACTOR = 2;

/** Construit les doutes à partir du résultat local et des entités scannées. */
export function buildAnalysisDoubts(
  result: PlanAnalysisResult,
  entities: AnalysisEntity[],
  planDiagonal: number,
): AnalysisDoubt[] {
  const doubts: AnalysisDoubt[] = [];
  const byHandle = new Map(entities.map((e) => [e.handle, e]));
  const disorderGroups = result.groups.filter((g) => g.kind === "disorder");

  // ── Groupes du même désordre relativement proches ─────────────────────────
  const nearLimit = NEAR_GAP_MAX_RATIO * planDiagonal * NEAR_GROUPS_DOUBT_FACTOR;
  for (let i = 0; i < disorderGroups.length; i++) {
    for (let j = i + 1; j < disorderGroups.length; j++) {
      const a = disorderGroups[i];
      const b = disorderGroups[j];
      if (a.defectName === null || a.defectName !== b.defectName) continue;
      // Deux vignettes différentes = deux vues : la proximité sur la feuille
      // est un artefact de mise en page, pas un doute.
      if (a.vignetteId && b.vignetteId && a.vignetteId !== b.vignetteId) {
        continue;
      }
      const gap = bboxGap(a.bbox, b.bbox);
      if (gap > nearLimit) continue;
      doubts.push({
        type: "near_groups",
        message:
          `Deux groupes « ${a.defectName} » sont relativement proches : ` +
          `séparés par prudence (espace > taille des tracés), mais peut-être une seule observation.`,
        entityIds: [...a.handles, ...b.handles],
      });
    }
  }

  // ── Textes multi-leaders ──────────────────────────────────────────────────
  for (const entity of entities) {
    if ((entity.leaderTips?.length ?? 0) >= 2 && entity.text?.trim()) {
      doubts.push({
        type: "multi_tip_text",
        message:
          `Le texte « ${entity.text?.trim()} » pointe ${entity.leaderTips?.length} zones ` +
          `via ses leaders : une seule observation étendue, ou plusieurs ?`,
        entityIds: [entity.handle],
      });
    }
  }

  // ── Groupes trop photographiés ────────────────────────────────────────────
  for (const group of disorderGroups) {
    if (group.markerNumbers.length >= MANY_MARKERS_COUNT) {
      doubts.push({
        type: "many_markers",
        message:
          `Un groupe porte ${group.markerNumbers.length} photos (1 à 2 est la norme) : ` +
          `certaines appartiennent peut-être à des observations voisines.`,
        entityIds: group.markerHandles,
      });
    }
  }

  // ── Orphelins presque rattachables ────────────────────────────────────────
  const orphanLimit = ATTACH_GAP_RATIO * planDiagonal * ORPHAN_DOUBT_FACTOR;
  const orphanHandles = [
    ...result.unassignedTextHandles,
    ...result.unassignedMarkerHandles,
  ];
  for (const handle of orphanHandles) {
    const entity = byHandle.get(handle);
    if (!entity?.bbox) continue;
    const nearest = Math.min(
      ...result.groups.map((g) => bboxGap(entity.bbox as WorldBbox, g.bbox)),
      Number.POSITIVE_INFINITY,
    );
    if (nearest > orphanLimit) continue;
    const role = classifyEntity(entity) === "marker" ? "marqueur photo" : "texte";
    doubts.push({
      type: "orphan_near",
      message:
        `Un ${role} est resté orphelin mais n'est pas si loin d'un groupe : ` +
        `rattachement volontairement prudent, à toi de juger.`,
      entityIds: [handle],
    });
  }

  // ── Photos à trancher ─────────────────────────────────────────────────────
  // La passe photos n'a pas su départager plusieurs groupes (ni la visée, ni
  // l'espace, ni le temps) : rattachée par défaut au meilleur, l'IA choisit
  // parmi les candidats listés dans la section PHOTOS du payload.
  for (const group of result.groups) {
    for (const photo of group.photoAttachments ?? []) {
      if (photo.rule !== "ambiguous") continue;
      doubts.push({
        type: "photo_candidates",
        message:
          `La photo ${photo.markerNumber ?? "?"} est à distance comparable de ` +
          `${photo.alternatives.length + 1} groupes : rattachée par défaut au ` +
          `plus proche, à trancher parmi ses candidats (section PHOTOS).`,
        entityIds: [photo.handle],
      });
    }
  }

  return doubts;
}
